import { useMemo } from 'react';
import {
  createRtkPreloadArgsHash,
  createRtkPreloadRunner,
  createRtkPreloadedModuleSelector,
  buildRunReportsPreloadOperations,
  defineRtkPreloadModule,
  defineRtkPreloadOperation,
  shouldRenderPreloadedContent,
  type RtkPreloadEndpoint,
  type RtkPreloadEndpointSelection,
  type RtkPreloadModuleDescriptor,
  type RtkPreloadOperationDescriptor,
  type RtkPreloadPhase,
  type RtkPreloadRuntimeContext,
  type RtkPreloadTelemetryEvent,
  type RtkPreloadedModuleReadyState,
  type RtkPreloadedOperationReadyState,
} from '@triedge/rtk-abstractions';
import { domainRouterApi } from '../state/domainRouterApi';
import { domainRouterUniversalApi } from '../state/domainRouterUniversalApi';
import { useAppSelector } from '../state/hooks';
import type { RootState } from '../state/store';
import { ACTIVE_TUITION_ACADEMIC_YEAR } from '../internal-owned/tuition/shared/academicYear';
import contract from './ystnjWorkspacePreloadContract.json';

export type YstnjWorkspacePreloadModuleId =
  | 'admissions'
  | 'financial-aid'
  | 'settings'
  | 'students-families'
  | 'parent-portal'
  | 'tuition'
  | 'run-reports';

export type YstnjWorkspacePreloadContext = RtkPreloadRuntimeContext & {
  actorId?: string | null;
  persona: string | null;
  routePath: string;
};

export type YstnjWorkspacePreloadEvent = RtkPreloadTelemetryEvent;
type AppSelector<T> = (state: RootState) => T;

export const YSTNJ_WORKSPACE_PRELOAD_ENV_FLAG = 'VITE_YSTNJ_WORKSPACE_PRELOAD';
export const YSTNJ_WORKSPACE_PRELOAD_STORAGE_KEY = 'ystnj.workspacePreload.enabled';
export const YSTNJ_WORKSPACE_PRELOAD_DEBUG_STORAGE_KEY = 'ystnj.workspacePreload.debug';
export const YSTNJ_WORKSPACE_PRELOAD_REQUIRED_MODULES =
  contract.requiredModules as ReadonlyArray<Exclude<YstnjWorkspacePreloadModuleId, 'tuition'>>;
export const YSTNJ_WORKSPACE_PRELOAD_SUPPLEMENTAL_MODULES =
  contract.supplementalModules as ReadonlyArray<YstnjWorkspacePreloadModuleId>;
export const YSTNJ_WORKSPACE_PRELOAD_CONTRACT = contract;

declare global {
  interface Window {
    __YSTNJ_WORKSPACE_PRELOAD_EVENTS__?: YstnjWorkspacePreloadEvent[];
    __YSTNJ_WORKSPACE_PRELOAD_DEBUG__?: YstnjWorkspacePreloadDebugBridge;
  }
}

type UniversalDomainQueryRequest = {
  operation: string;
  args: unknown;
};

const PAGE_SIZE = 200;
const STAFF_USERS_PAGE_SIZE = 20;
const TUITION_DASHBOARD_PAGE_SIZE = 100;
const NOTIFICATION_PAGE_SIZE = 50;
const SETTINGS_MODULE_KEY = 'staff_settings';
const STAFF_PERSONAS = new Set(['staff', 'administrator', 'super-administrator']);
const ADMINISTRATOR_PERSONAS = new Set(['administrator', 'super-administrator']);
const EAGER_CACHE_POLICY = {
  maxAgeMs: 60_000,
  staleRefresh: true,
  staleWhileRevalidate: true,
} as const;

const readBooleanOverride = (value: string | null | undefined): boolean | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) {
    return false;
  }
  return null;
};

export const isYstnjWorkspacePreloadEnabled = (): boolean => {
  if (typeof window !== 'undefined') {
    const localOverride = readBooleanOverride(window.localStorage.getItem(YSTNJ_WORKSPACE_PRELOAD_STORAGE_KEY));
    if (localOverride != null) {
      return localOverride;
    }
  }

  const envOverride = readBooleanOverride(import.meta.env.VITE_YSTNJ_WORKSPACE_PRELOAD);
  if (envOverride != null) {
    return envOverride;
  }

  return false;
};

export const isYstnjWorkspacePreloadDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return readBooleanOverride(window.localStorage.getItem(YSTNJ_WORKSPACE_PRELOAD_DEBUG_STORAGE_KEY)) === true;
};

const asEndpoint = <Args>(
  endpointId: string,
  endpoint: unknown,
): RtkPreloadEndpoint<Args, RootState> => {
  const rtkEndpoint = endpoint as Partial<RtkPreloadEndpoint<Args, RootState>>;
  if (typeof rtkEndpoint.select !== 'function' || typeof rtkEndpoint.initiate !== 'function') {
    throw new Error(`YSTNJ workspace preload endpoint ${endpointId} is not an RTK endpoint.`);
  }
  return {
    endpointId,
    select: rtkEndpoint.select,
    initiate: rtkEndpoint.initiate,
  };
};

const asVoidEndpoint = <Args extends Record<string, never>>(
  endpointId: string,
  endpoint: unknown,
): RtkPreloadEndpoint<Args, RootState> => {
  const rtkEndpoint = endpoint as {
    select?: (args: undefined) => (state: RootState) => unknown;
    initiate?: (
      args: undefined,
      options?: { subscribe?: boolean; forceRefetch?: boolean }
    ) => unknown;
  };
  if (typeof rtkEndpoint.select !== 'function' || typeof rtkEndpoint.initiate !== 'function') {
    throw new Error(`YSTNJ workspace preload endpoint ${endpointId} is not an RTK endpoint.`);
  }
  return {
    endpointId,
    select: () => rtkEndpoint.select?.(undefined) as (state: RootState) => RtkPreloadEndpointSelection,
    initiate: (_args, options) => rtkEndpoint.initiate?.(undefined, options),
  };
};

const universalDomainQueryEndpoint = asEndpoint<UniversalDomainQueryRequest>(
  'domainRouterUniversalApi.domainQuery',
  domainRouterUniversalApi.endpoints.domainQuery,
);

const domainEndpoints = domainRouterApi.endpoints as Record<string, unknown>;

const isStaffPersona = (context: YstnjWorkspacePreloadContext) =>
  Boolean(context.persona && STAFF_PERSONAS.has(context.persona));

const isAdministratorPersona = (context: YstnjWorkspacePreloadContext) =>
  Boolean(context.persona && ADMINISTRATOR_PERSONAS.has(context.persona));

const isParentPersona = (context: YstnjWorkspacePreloadContext) =>
  context.persona === 'parent';

const hasParentActor = (context: YstnjWorkspacePreloadContext) =>
  isParentPersona(context) && Boolean(context.actorId);

const readRouteParam = (context: YstnjWorkspacePreloadContext, key: string): string | null => {
  const value = context.routeParams?.[key];
  return value && value.trim().length > 0 ? value : null;
};

const operation = <Args>(
  descriptor: RtkPreloadOperationDescriptor<YstnjWorkspacePreloadContext, Args, RootState>,
) => defineRtkPreloadOperation(descriptor);

const admissionsOperations = [
  operation({
    moduleId: 'admissions',
    operationId: 'admissions.listApplications.page1',
    endpointId: 'domainRouterUniversalApi.domainQuery:listApplications',
    phase: 'eager',
    endpoint: universalDomainQueryEndpoint,
    buildArgs: () => ({ operation: 'listApplications', args: { limit: PAGE_SIZE } }),
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize: PAGE_SIZE, inlineContinuation: true },
    criticality: 'critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: isStaffPersona,
    skipReason: 'persona-not-authorized-for-school-module',
  }),
  operation({
    moduleId: 'admissions',
    operationId: 'admissions.getApplication.detail',
    endpointId: 'domainRouterApi.getAdmissionsApplication',
    phase: 'inline',
    endpoint: asEndpoint<string>(
      'domainRouterApi.getAdmissionsApplication',
      domainEndpoints.getAdmissionsApplication,
    ),
    buildArgs: (context) => readRouteParam(context, 'applicationId') ?? readRouteParam(context, 'familyId'),
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'route-param-applicationId-required',
  }),
  operation({
    moduleId: 'admissions',
    operationId: 'admissions.listApplications.nextPage',
    endpointId: 'domainRouterUniversalApi.domainQuery:listApplications',
    phase: 'pagination',
    endpoint: universalDomainQueryEndpoint,
    buildArgs: (context) => {
      const nextToken = readRouteParam(context, 'nextToken');
      return nextToken
        ? { operation: 'listApplications', args: { limit: PAGE_SIZE, nextToken } }
        : null;
    },
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize: PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'inline-pagination-token-required',
  }),
];

const financialAidOperations = [
  operation({
    moduleId: 'financial-aid',
    operationId: 'financial-aid.listReviewItems.page1',
    endpointId: 'domainRouterApi.listFinancialAidApplicationsForReview',
    phase: 'eager',
    endpoint: asEndpoint<{ limit: number }>(
      'domainRouterApi.listFinancialAidApplicationsForReview',
      domainEndpoints.listFinancialAidApplicationsForReview,
    ),
    buildArgs: () => ({ limit: PAGE_SIZE }),
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize: PAGE_SIZE, inlineContinuation: true },
    criticality: 'critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: isStaffPersona,
    skipReason: 'persona-not-authorized-for-school-module',
  }),
  operation({
    moduleId: 'financial-aid',
    operationId: 'financial-aid.getReviewDetail.detail',
    endpointId: 'domainRouterApi.getFinancialAidApplicationForReview',
    phase: 'inline',
    endpoint: asEndpoint<string>(
      'domainRouterApi.getFinancialAidApplicationForReview',
      domainEndpoints.getFinancialAidApplicationForReview,
    ),
    buildArgs: (context) => readRouteParam(context, 'applicationId'),
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'route-param-applicationId-required',
  }),
  operation({
    moduleId: 'financial-aid',
    operationId: 'financial-aid.listReviewItems.nextPage',
    endpointId: 'domainRouterApi.listFinancialAidApplicationsForReview',
    phase: 'pagination',
    endpoint: asEndpoint<{ limit: number; nextToken?: string | null }>(
      'domainRouterApi.listFinancialAidApplicationsForReview',
      domainEndpoints.listFinancialAidApplicationsForReview,
    ),
    buildArgs: (context) => {
      const nextToken = readRouteParam(context, 'nextToken');
      return nextToken ? { limit: PAGE_SIZE, nextToken } : null;
    },
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize: PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'inline-pagination-token-required',
  }),
];

const settingsOperations = [
  operation({
    moduleId: 'settings',
    operationId: 'settings.getModuleConfig',
    endpointId: 'domainRouterApi.getSettingsModuleConfig',
    phase: 'eager',
    endpoint: asEndpoint<{ moduleKey: string }>(
      'domainRouterApi.getSettingsModuleConfig',
      domainEndpoints.getSettingsModuleConfig,
    ),
    buildArgs: () => ({ moduleKey: SETTINGS_MODULE_KEY }),
    pagination: { mode: 'none' },
    criticality: 'critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: isAdministratorPersona,
    skipReason: 'persona-not-authorized-for-settings-module',
  }),
  operation({
    moduleId: 'settings',
    operationId: 'settings.listStaffUsers.page1',
    endpointId: 'domainRouterApi.listStaffUsers',
    phase: 'eager',
    endpoint: asEndpoint<{ statusFilter: 'all'; page: number; limit: number }>(
      'domainRouterApi.listStaffUsers',
      domainEndpoints.listStaffUsers,
    ),
    buildArgs: () => ({ statusFilter: 'all' as const, page: 1, limit: STAFF_USERS_PAGE_SIZE }),
    pagination: { mode: 'offset', eagerPageLimit: 1, pageSize: STAFF_USERS_PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: isAdministratorPersona,
    skipReason: 'persona-not-authorized-for-settings-module',
  }),
  operation({
    moduleId: 'settings',
    operationId: 'settings.getModuleConfig.section',
    endpointId: 'domainRouterApi.getSettingsModuleConfig',
    phase: 'inline',
    endpoint: asEndpoint<{ moduleKey: string }>(
      'domainRouterApi.getSettingsModuleConfig',
      domainEndpoints.getSettingsModuleConfig,
    ),
    buildArgs: () => ({ moduleKey: SETTINGS_MODULE_KEY }),
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: isAdministratorPersona,
    skipReason: 'persona-not-authorized-for-settings-module',
  }),
  operation({
    moduleId: 'settings',
    operationId: 'settings.listStaffUsers.nextPage',
    endpointId: 'domainRouterApi.listStaffUsers',
    phase: 'pagination',
    endpoint: asEndpoint<{ statusFilter: 'all'; page: number; limit: number }>(
      'domainRouterApi.listStaffUsers',
      domainEndpoints.listStaffUsers,
    ),
    buildArgs: (context) => {
      const page = Number(readRouteParam(context, 'page'));
      return Number.isFinite(page) && page > 1 ? { statusFilter: 'all' as const, page, limit: STAFF_USERS_PAGE_SIZE } : null;
    },
    pagination: { mode: 'offset', eagerPageLimit: 1, pageSize: STAFF_USERS_PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    enabled: isAdministratorPersona,
    skipReason: 'inline-pagination-page-required',
  }),
];

const studentsFamiliesOperations = [
  operation({
    moduleId: 'students-families',
    operationId: 'students-families.dashboardTree.page1',
    endpointId: 'domainRouterUniversalApi.domainQuery:getStudentsDashboardTree',
    phase: 'eager',
    endpoint: universalDomainQueryEndpoint,
    buildArgs: () => ({ operation: 'getStudentsDashboardTree', args: { limit: PAGE_SIZE, offset: 0 } }),
    pagination: { mode: 'offset', eagerPageLimit: 1, pageSize: PAGE_SIZE, inlineContinuation: true },
    criticality: 'critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: isStaffPersona,
    skipReason: 'persona-not-authorized-for-school-module',
  }),
  operation({
    moduleId: 'students-families',
    operationId: 'students-families.familyDetail.detail',
    endpointId: 'domainRouterUniversalApi.domainQuery:getFamilyDetailCoreTree',
    phase: 'inline',
    endpoint: universalDomainQueryEndpoint,
    buildArgs: (context) => {
      const familyId = readRouteParam(context, 'familyId');
      return familyId
        ? { operation: 'getFamilyDetailCoreTree', args: { familyId } }
        : null;
    },
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'route-param-familyId-required',
  }),
  operation({
    moduleId: 'students-families',
    operationId: 'students-families.listFamilies.nextPage',
    endpointId: 'domainRouterUniversalApi.domainQuery:listFamilies',
    phase: 'pagination',
    endpoint: universalDomainQueryEndpoint,
    buildArgs: (context) => {
      const offset = Number(readRouteParam(context, 'offset'));
      return Number.isFinite(offset) && offset > 0
        ? { operation: 'listFamilies', args: { input: JSON.stringify({ limit: PAGE_SIZE, offset }) } }
        : null;
    },
    pagination: { mode: 'offset', eagerPageLimit: 1, pageSize: PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'inline-pagination-offset-required',
  }),
];

const parentPortalOperations = [
  operation({
    moduleId: 'parent-portal',
    operationId: 'parent-portal.listStudentProfiles.page1',
    endpointId: 'domainRouterApi.listPortalStudentProfiles',
    phase: 'eager',
    endpoint: asVoidEndpoint<Record<string, never>>(
      'domainRouterApi.listPortalStudentProfiles',
      domainEndpoints.listPortalStudentProfiles,
    ),
    buildArgs: (context) => (context.actorId ? {} : null),
    pagination: { mode: 'none' },
    criticality: 'critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: hasParentActor,
    skipReason: 'parent-actor-required',
  }),
  operation({
    moduleId: 'parent-portal',
    operationId: 'parent-portal.listNotifications.page1',
    endpointId: 'domainRouterApi.listNotifications',
    phase: 'eager',
    endpoint: asEndpoint<{ actorId: string; includeRead: boolean; limit: number }>(
      'domainRouterApi.listNotifications',
      domainEndpoints.listNotifications,
    ),
    buildArgs: (context) => context.actorId
      ? { actorId: context.actorId, includeRead: true, limit: 5 }
      : null,
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize: NOTIFICATION_PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: hasParentActor,
    skipReason: 'parent-actor-required',
  }),
  operation({
    moduleId: 'parent-portal',
    operationId: 'parent-portal.listCalendar.inline',
    endpointId: 'domainRouterApi.listPortalCalendarEvents',
    phase: 'inline',
    endpoint: asVoidEndpoint<Record<string, never>>(
      'domainRouterApi.listPortalCalendarEvents',
      domainEndpoints.listPortalCalendarEvents,
    ),
    buildArgs: (context) => (context.actorId ? {} : null),
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: hasParentActor,
    skipReason: 'parent-actor-required',
  }),
  operation({
    moduleId: 'parent-portal',
    operationId: 'parent-portal.listResources.inline',
    endpointId: 'domainRouterApi.listPortalResources',
    phase: 'inline',
    endpoint: asVoidEndpoint<Record<string, never>>(
      'domainRouterApi.listPortalResources',
      domainEndpoints.listPortalResources,
    ),
    buildArgs: (context) => (context.actorId ? {} : null),
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: hasParentActor,
    skipReason: 'parent-actor-required',
  }),
  operation({
    moduleId: 'parent-portal',
    operationId: 'parent-portal.listNotifications.nextPage',
    endpointId: 'domainRouterApi.listNotifications',
    phase: 'pagination',
    endpoint: asEndpoint<{ actorId: string; includeRead: boolean; limit: number; nextToken: string }>(
      'domainRouterApi.listNotifications',
      domainEndpoints.listNotifications,
    ),
    buildArgs: (context) => {
      const nextToken = readRouteParam(context, 'nextToken');
      return context.actorId && nextToken
        ? { actorId: context.actorId, includeRead: true, limit: NOTIFICATION_PAGE_SIZE, nextToken }
        : null;
    },
    pagination: { mode: 'cursor', eagerPageLimit: 1, pageSize: NOTIFICATION_PAGE_SIZE, inlineContinuation: true },
    criticality: 'non-critical',
    enabled: hasParentActor,
    skipReason: 'inline-pagination-token-required',
  }),
];

const tuitionOperations = [
  operation({
    moduleId: 'tuition',
    operationId: 'tuition.schoolDashboard.page1',
    endpointId: 'domainRouterApi.getSchoolTuitionDashboardTree',
    phase: 'eager',
    endpoint: asEndpoint<{ academicYear: string; limit: number; offset: number }>(
      'domainRouterApi.getSchoolTuitionDashboardTree',
      domainEndpoints.getSchoolTuitionDashboardTree,
    ),
    buildArgs: () => ({
      academicYear: ACTIVE_TUITION_ACADEMIC_YEAR,
      limit: TUITION_DASHBOARD_PAGE_SIZE,
      offset: 0,
    }),
    pagination: {
      mode: 'offset',
      eagerPageLimit: 1,
      pageSize: TUITION_DASHBOARD_PAGE_SIZE,
      inlineContinuation: true,
    },
    criticality: 'critical',
    cachePolicy: EAGER_CACHE_POLICY,
    enabled: isStaffPersona,
    skipReason: 'persona-not-authorized-for-school-module',
  }),
  operation({
    moduleId: 'tuition',
    operationId: 'tuition.familyLedger.detail',
    endpointId: 'domainRouterApi.getSchoolTuitionFamilyLedgerTree',
    phase: 'inline',
    endpoint: asEndpoint<{ academicYear: string; familyId: string }>(
      'domainRouterApi.getSchoolTuitionFamilyLedgerTree',
      domainEndpoints.getSchoolTuitionFamilyLedgerTree,
    ),
    buildArgs: (context) => {
      const familyId = readRouteParam(context, 'familyId');
      return familyId
        ? { academicYear: ACTIVE_TUITION_ACADEMIC_YEAR, familyId }
        : null;
    },
    pagination: { mode: 'none' },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'route-param-familyId-required',
  }),
  operation({
    moduleId: 'tuition',
    operationId: 'tuition.schoolDashboard.nextPage',
    endpointId: 'domainRouterApi.getSchoolTuitionDashboardTree',
    phase: 'pagination',
    endpoint: asEndpoint<{ academicYear: string; limit: number; offset: number }>(
      'domainRouterApi.getSchoolTuitionDashboardTree',
      domainEndpoints.getSchoolTuitionDashboardTree,
    ),
    buildArgs: (context) => {
      const offset = Number(readRouteParam(context, 'offset'));
      return Number.isFinite(offset) && offset > 0
        ? {
            academicYear: ACTIVE_TUITION_ACADEMIC_YEAR,
            limit: TUITION_DASHBOARD_PAGE_SIZE,
            offset,
          }
        : null;
    },
    pagination: {
      mode: 'offset',
      eagerPageLimit: 1,
      pageSize: TUITION_DASHBOARD_PAGE_SIZE,
      inlineContinuation: true,
    },
    criticality: 'non-critical',
    enabled: isStaffPersona,
    skipReason: 'inline-pagination-offset-required',
  }),
];

const runReportsOperations = buildRunReportsPreloadOperations<
  YstnjWorkspacePreloadContext,
  UniversalDomainQueryRequest,
  RootState
>({
  endpoint: universalDomainQueryEndpoint,
  endpointIdPrefix: 'domainRouterUniversalApi.domainQuery',
  enabled: isStaffPersona,
  readRouteParam,
  pageSize: PAGE_SIZE,
  eagerCachePolicy: EAGER_CACHE_POLICY,
});

export const ystnjWorkspacePreloadModules: ReadonlyArray<
  RtkPreloadModuleDescriptor<YstnjWorkspacePreloadContext, RootState>
> = [
  defineRtkPreloadModule({ moduleId: 'admissions', operations: admissionsOperations }),
  defineRtkPreloadModule({ moduleId: 'financial-aid', operations: financialAidOperations }),
  defineRtkPreloadModule({ moduleId: 'settings', operations: settingsOperations }),
  defineRtkPreloadModule({ moduleId: 'students-families', operations: studentsFamiliesOperations }),
  defineRtkPreloadModule({ moduleId: 'parent-portal', operations: parentPortalOperations }),
  defineRtkPreloadModule({ moduleId: 'tuition', operations: tuitionOperations }),
  defineRtkPreloadModule({ moduleId: 'run-reports', operations: runReportsOperations }),
];

export const ystnjWorkspacePreloadOperations = ystnjWorkspacePreloadModules
  .flatMap((module) => module.operations);

export const resolveYstnjWorkspacePreloadModuleForPath = (
  pathname: string,
): YstnjWorkspacePreloadModuleId | null => {
  if (pathname.startsWith('/school/admissions')) {
    return 'admissions';
  }
  if (pathname.startsWith('/school/financialaid')) {
    return 'financial-aid';
  }
  if (pathname.startsWith('/school/settings')) {
    return 'settings';
  }
  if (pathname.startsWith('/school/students')) {
    return 'students-families';
  }
  if (pathname.startsWith('/parent/portal')) {
    return 'parent-portal';
  }
  if (pathname.startsWith('/school/tuition')) {
    return 'tuition';
  }
  if (pathname.startsWith('/school/run-reports')) {
    return 'run-reports';
  }
  return null;
};

export const resolveYstnjWorkspacePreloadRouteParams = (
  pathname: string,
  search = '',
): Record<string, string | undefined> => {
  const query = new URLSearchParams(search);
  const params: Record<string, string | undefined> = {};
  const nextToken = query.get('nextToken');
  const page = query.get('page');
  const offset = query.get('offset');
  if (nextToken) {
    params.nextToken = nextToken;
  }
  if (page) {
    params.page = page;
  }
  if (offset) {
    params.offset = offset;
  }

  const admissionsMatch = pathname.match(/^\/school\/admissions\/(?:children|family|documents|notes|tuition)\/([^/]+)/);
  if (admissionsMatch?.[1]) {
    params.applicationId = decodeURIComponent(admissionsMatch[1]);
    params.familyId = decodeURIComponent(admissionsMatch[1]);
    return params;
  }

  const financialAidMatch = pathname.match(/^\/school\/financialaid\/staff\/([^/]+)/);
  if (financialAidMatch?.[1]) {
    params.applicationId = decodeURIComponent(financialAidMatch[1]);
    return params;
  }

  const studentsMatch = pathname.match(/^\/school\/students\/family\/([^/]+)/);
  if (studentsMatch?.[1]) {
    params.familyId = decodeURIComponent(studentsMatch[1]);
    return params;
  }

  const tuitionMatch = pathname.match(/^\/school\/tuition\/family\/([^/]+)/);
  if (tuitionMatch?.[1]) {
    params.familyId = decodeURIComponent(tuitionMatch[1]);
    return params;
  }

  return params;
};

const isFulfilledSelectionForDebug = (selection: RtkPreloadEndpointSelection): boolean =>
  selection.isSuccess === true
  || selection.status === 'fulfilled'
  || selection.status === 'success'
  || selection.data !== undefined;

const cacheStatusForDebug = (
  selection: RtkPreloadEndpointSelection,
  maxAgeMs: number | undefined,
  nowMs: number,
) => {
  if (!isFulfilledSelectionForDebug(selection)) {
    return 'miss' as const;
  }
  if (!maxAgeMs || !selection.fulfilledTimeStamp) {
    return 'hit' as const;
  }
  return nowMs - selection.fulfilledTimeStamp > maxAgeMs ? 'stale' as const : 'hit' as const;
};

export type YstnjWorkspacePreloadDebugOperationSnapshot = {
  moduleId: string;
  operationId: string;
  endpointId: string;
  phase: RtkPreloadPhase;
  criticality: string;
  argsHash: string;
  selectorStatus: string | null;
  cacheStatus: 'hit' | 'miss' | 'stale' | 'unknown';
  hasData: boolean;
  isLoading: boolean;
  isFetching: boolean;
  fulfilledTimeStamp: number | null;
  skipReason: string | null;
};

export type YstnjWorkspacePreloadDebugSnapshot = {
  artifactType: 'YstnjWorkspacePreloadDebugSnapshot';
  generatedAt: string;
  routePath: string;
  persona: string | null;
  actorPresent: boolean;
  operationCount: number;
  operations: YstnjWorkspacePreloadDebugOperationSnapshot[];
};

export type YstnjWorkspacePreloadDebugSnapshotOptions = Partial<YstnjWorkspacePreloadContext> & {
  phases?: ReadonlyArray<RtkPreloadPhase>;
  moduleIds?: ReadonlyArray<YstnjWorkspacePreloadModuleId>;
  operationIds?: ReadonlyArray<string>;
};

export type YstnjWorkspacePreloadDebugBridge = {
  snapshot: (options?: YstnjWorkspacePreloadDebugSnapshotOptions) => Promise<YstnjWorkspacePreloadDebugSnapshot>;
};

export const createYstnjWorkspacePreloadDebugBridge = ({
  getState,
  getContext,
}: {
  getState: () => RootState;
  getContext: () => YstnjWorkspacePreloadContext;
}): YstnjWorkspacePreloadDebugBridge => ({
  snapshot: async (options = {}) => {
    const context = {
      ...getContext(),
      ...options,
      routeParams: options.routeParams ?? resolveYstnjWorkspacePreloadRouteParams(
        options.routePath ?? getContext().routePath,
      ),
    };
    const phaseFilter = options.phases ? new Set(options.phases) : null;
    const moduleFilter = options.moduleIds ? new Set<string>(options.moduleIds) : null;
    const operationFilter = options.operationIds ? new Set(options.operationIds) : null;
    const operations = await Promise.all(
      ystnjWorkspacePreloadOperations
        .filter((entry) => !phaseFilter || phaseFilter.has(entry.phase))
        .filter((entry) => !moduleFilter || moduleFilter.has(entry.moduleId))
        .filter((entry) => !operationFilter || operationFilter.has(entry.operationId))
        .map(async (entry): Promise<YstnjWorkspacePreloadDebugOperationSnapshot> => {
          const enabled = entry.enabled ? await entry.enabled(context) : true;
          if (!enabled) {
            return {
              moduleId: entry.moduleId,
              operationId: entry.operationId,
              endpointId: entry.endpointId,
              phase: entry.phase,
              criticality: entry.criticality,
              argsHash: 'args_skipped',
              selectorStatus: null,
              cacheStatus: 'unknown',
              hasData: false,
              isLoading: false,
              isFetching: false,
              fulfilledTimeStamp: null,
              skipReason: typeof entry.skipReason === 'function'
                ? entry.skipReason(context)
                : entry.skipReason ?? 'operation-disabled',
            };
          }
          const args = await entry.buildArgs(context);
          if (args == null) {
            return {
              moduleId: entry.moduleId,
              operationId: entry.operationId,
              endpointId: entry.endpointId,
              phase: entry.phase,
              criticality: entry.criticality,
              argsHash: 'args_null',
              selectorStatus: null,
              cacheStatus: 'unknown',
              hasData: false,
              isLoading: false,
              isFetching: false,
              fulfilledTimeStamp: null,
              skipReason: typeof entry.skipReason === 'function'
                ? entry.skipReason(context)
                : entry.skipReason ?? 'args-unavailable',
            };
          }
          const argsHash = createRtkPreloadArgsHash(args);
          const selection = entry.endpoint.select(args)(getState());
          return {
            moduleId: entry.moduleId,
            operationId: entry.operationId,
            endpointId: entry.endpointId,
            phase: entry.phase,
            criticality: entry.criticality,
            argsHash,
            selectorStatus: selection.status ?? null,
            cacheStatus: cacheStatusForDebug(selection, entry.cachePolicy?.maxAgeMs, Date.now()),
            hasData: selection.data !== undefined,
            isLoading: selection.isLoading === true,
            isFetching: selection.isFetching === true,
            fulfilledTimeStamp: typeof selection.fulfilledTimeStamp === 'number'
              ? selection.fulfilledTimeStamp
              : null,
            skipReason: null,
          };
        }),
    );

    return {
      artifactType: 'YstnjWorkspacePreloadDebugSnapshot',
      generatedAt: new Date().toISOString(),
      routePath: context.routePath,
      persona: context.persona,
      actorPresent: Boolean(context.actorId),
      operationCount: operations.length,
      operations,
    };
  },
});

export const createYstnjWorkspacePreloadRunner = ({
  dispatch,
  getState,
  onTelemetry,
}: {
  dispatch: (action: unknown) => unknown;
  getState: () => RootState;
  onTelemetry: (event: YstnjWorkspacePreloadEvent) => void;
}) => createRtkPreloadRunner<YstnjWorkspacePreloadContext, RootState>({
  modules: ystnjWorkspacePreloadModules,
  dispatch,
  getState,
  concurrency: 3,
  defaultTimeoutMs: 10_000,
  onTelemetry,
});

export const recordYstnjWorkspacePreloadEvent = (
  event: YstnjWorkspacePreloadEvent,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const events = window.__YSTNJ_WORKSPACE_PRELOAD_EVENTS__ ?? [];
  events.push(event);
  window.__YSTNJ_WORKSPACE_PRELOAD_EVENTS__ = events.slice(-500);
  window.dispatchEvent(new CustomEvent('ystnj:workspace-preload', { detail: event }));
};

export const selectYstnjPreloadOperationsForRoute = (
  pathname: string,
  phase: RtkPreloadPhase,
): string[] => {
  const moduleId = resolveYstnjWorkspacePreloadModuleForPath(pathname);
  if (!moduleId) {
    return [];
  }
  return ystnjWorkspacePreloadOperations
    .filter((entry) => entry.moduleId === moduleId && entry.phase === phase)
    .map((entry) => entry.operationId);
};

export const selectYstnjCriticalPreloadOperationsForRoute = (
  pathname: string,
  phase: RtkPreloadPhase = 'eager',
): string[] => {
  const moduleId = resolveYstnjWorkspacePreloadModuleForPath(pathname);
  if (!moduleId) {
    return [];
  }
  return ystnjWorkspacePreloadOperations
    .filter((entry) => (
      entry.moduleId === moduleId
      && entry.phase === phase
      && entry.criticality === 'critical'
    ))
    .map((entry) => entry.operationId);
};

export type {
  RtkPreloadedModuleReadyState,
  RtkPreloadedOperationReadyState,
};

export type YstnjPreloadedModuleReadyState = RtkPreloadedModuleReadyState;

const getEndpointForYstnjOp = (
  op: RtkPreloadOperationDescriptor<YstnjWorkspacePreloadContext, any, RootState>,
): RtkPreloadEndpoint<any, RootState> => op.endpoint;

const splitRoutePathAndSearch = (routePath: string): { pathname: string; search: string } => {
  const [pathname = '', search = ''] = routePath.split('?');
  return {
    pathname,
    search: search ? `?${search}` : '',
  };
};

const resolveBrowserRoute = (): { pathname: string; search: string } => {
  if (typeof window === 'undefined') {
    return { pathname: '', search: '' };
  }
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
};

const resolveYstnjPreloadSelectorContext = (
  state: RootState,
  contextOverride: Partial<YstnjWorkspacePreloadContext> = {},
): YstnjWorkspacePreloadContext => {
  const browserRoute = resolveBrowserRoute();
  const storedRoute = splitRoutePathAndSearch(state.shell.lastVisitedPath ?? browserRoute.pathname);
  const routePath = contextOverride.routePath ?? storedRoute.pathname;
  const search = storedRoute.search || browserRoute.search;
  return {
    ...contextOverride,
    routePath,
    persona: contextOverride.persona ?? state.shell.personaOverride ?? null,
    routeParams: contextOverride.routeParams ?? resolveYstnjWorkspacePreloadRouteParams(routePath, search),
  };
};

export const createYstnjPreloadedModuleSelector = (
  moduleId: YstnjWorkspacePreloadModuleId,
  contextOverride: Partial<YstnjWorkspacePreloadContext> = {},
): AppSelector<YstnjPreloadedModuleReadyState> => {
  const criticalOps = ystnjWorkspacePreloadOperations.filter(
    (op) => op.moduleId === moduleId && op.criticality === 'critical'
  );

  const baseSelector = createRtkPreloadedModuleSelector<
    YstnjWorkspacePreloadContext,
    RootState
  >(
    moduleId,
    criticalOps,
    getEndpointForYstnjOp,
    { treatStaleAsReady: true }
  );

  return (state: RootState) => {
    const context = resolveYstnjPreloadSelectorContext(state, contextOverride);
    return baseSelector(state, context);
  };
};

export const useYstnjPreloadedModule = (
  moduleId: YstnjWorkspacePreloadModuleId | null,
  contextOverride?: Partial<YstnjWorkspacePreloadContext>,
): YstnjPreloadedModuleReadyState | null => {
  const enabled = isYstnjWorkspacePreloadEnabled();
  const selector = useMemo(
    () => (moduleId ? createYstnjPreloadedModuleSelector(moduleId, contextOverride ?? {}) : null),
    [moduleId, contextOverride],
  );

  return useAppSelector((state: RootState) => {
    if (!enabled || !selector) {
      return null;
    }
    return selector(state);
  });
};

export const useShouldRenderYstnjPreloadedContent = (
  moduleId: YstnjWorkspacePreloadModuleId | null
): boolean => {
  const ready = useYstnjPreloadedModule(moduleId);
  if (!ready) return true;
  return shouldRenderPreloadedContent(ready);
};
